/**
 * 共通の設定メニュー（Chat / Board の両画面から到達）。
 * 「⚙ 設定」ボタン → メニュー（AIアシスタント設定 / ナレッジ）→ 各ダイアログ。
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import { formatSizeKB } from "@/lib/utils"
import { ChevronRight, ChevronDown, Plus, Trash2, Upload, Download } from "lucide-react"
import { PreviewModal, PreviewItem } from "@/components/common/PreviewModal"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AgentFace } from "@/components/AgentFace"
import {
  AgentProfile,
  KnowledgeItem,
  createKnowledgeTitle,
  deleteKnowledgeFile,
  deleteKnowledgeTitle,
  getKnowledgeScopes,
  listKnowledge,
  saveProfile,
  setKnowledgeMuted,
  tutorialChat,
  updateKnowledgeTitle,
  uploadAvatar,
  uploadKnowledgeFile,
  getKnowledgeFileUrl,
} from "@/services/boardService"

type ChatMsg = { role: "user" | "assistant"; text: string }

/** エージェントモーダル: 「記憶と顔」の管理に専念（自由会話は Chat に一本化）。
 *  チュートリアル（命名→インタビュー）／記憶の閲覧追加削除／顔（SVG ビルダー＋画像
 *  アップロード）／再インタビュー を旧 Flask UI のパリティで提供する。 */
export function AgentSettingsDialog({
  idToken,
  profile,
  avatarUrl,
  tutorial,
  onClose,
  asPage = false,
}: {
  idToken: string
  profile: AgentProfile | null
  avatarUrl: string
  tutorial: boolean
  onClose: () => void
  // asPage=true のときはモーダルではなくフルページとして本文だけ描画する（管理画面のルート用）。
  // ロジック・本文は共通（1箇所）で、外側のチローム（Dialog or ページ）だけ差し替える。
  asPage?: boolean
}) {
  const [nameInput, setNameInput] = useState("")
  const [naming, setNaming] = useState(tutorial)
  // 画面モード: setup=記憶と顔の管理 / interview=（再）インタビュー会話 / face=顔ビルダー
  const [view, setView] = useState<"setup" | "interview" | "face">("setup")
  const [interviewMode, setInterviewMode] = useState<"onboarding" | "revisit">("onboarding")
  const [agentName, setAgentName] = useState(profile?.agentName || "")
  const [iconSvg, setIconSvg] = useState(profile?.icon || "")
  const [avatarType, setAvatarType] = useState(profile?.avatar || "svg")
  const [msgs, setMsgs] = useState<ChatMsg[]>([])
  const [input, setInput] = useState("")
  const [busy, setBusy] = useState(false)
  const [memory, setMemory] = useState<string[]>(profile?.memory || [])
  const [newFact, setNewFact] = useState("")
  // 振り返りメール（日次・月次ROI）の受信可否。未設定はデフォルト ON。
  const [receiveReflectionEmail, setReceiveReflectionEmail] = useState(
    profile?.receiveReflectionEmail ?? true
  )
  const [note, setNote] = useState("")
  // 名前を付け直す（改名）
  const [renameTo, setRenameTo] = useState("")
  const [renameOrigin, setRenameOrigin] = useState("")
  // 顔候補の選択状態。kind: preset(プリセットN) / upload(このセッションでアップした画像) /
  // existing(現在保存済みの画像 avatar)。クリックは選択だけ、「この顔に決める」で確定。
  const [uploads, setUploads] = useState<{ url: string; file: File }[]>([])
  const [picked, setPicked] = useState<{ kind: "preset" | "upload" | "existing"; id: number } | null>(null)
  const avatarRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // プロファイルが非同期で後から届いた場合、名前・記憶・顔の表示を同期する
  // （設定ボタンを押した直後はまだ読込中で agentName が空のことがあるため）。
  useEffect(() => {
    if (!profile) return
    if (profile.agentName) setAgentName(profile.agentName)
    setIconSvg(profile.icon || "")
    setAvatarType(profile.avatar || "svg")
    setMemory(profile.memory || [])
    setReceiveReflectionEmail(profile.receiveReflectionEmail ?? true)
  }, [profile])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [msgs.length])

  // インタビュー開始時の挨拶（自由会話 agentChat は廃止＝Chat に一本化）
  useEffect(() => {
    if (view !== "interview" || msgs.length > 0 || !idToken) return
    const boot = async () => {
      setBusy(true)
      try {
        const r = await tutorialChat(idToken, agentName, [], interviewMode)
        setMsgs([{ role: "assistant", text: r.text }])
      } catch (e) {
        setNote(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    }
    boot()
    // 依存は意図的にこの3つに限定（boot 内で参照する他の値は再実行不要）。
    // react-hooks プラグインは未導入のため exhaustive-deps の disable 指定は付けない
    // （未定義ルールへの disable 指定は eslint がエラーにするため）。
  }, [view, agentName, idToken])

  const sendMessage = async (text: string) => {
    if (!text.trim() || busy) return
    const next: ChatMsg[] = [...msgs, { role: "user" as const, text }]
    setMsgs(next)
    setInput("")
    setBusy(true)
    try {
      const r = await tutorialChat(idToken, agentName, next, interviewMode)
      setMsgs([...next, { role: "assistant" as const, text: r.text }])
      if (r.done) {
        setMemory((m) => [...m, ...(r.facts || [])])
        setNote("インタビューの内容を記憶しました。")
        // 命名直後のオンボーディングは、インタビュー後に顔決めへ進む
        setView(interviewMode === "onboarding" ? "face" : "setup")
      }
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const send = () => sendMessage(input)

  // 再インタビューを中断する（旧 Flask の「ここまでにする」相当）。
  // それまでの回答を記憶に反映して終了する（モデルが finish_interview を呼ぶよう促す）。
  const finishInterview = () =>
    sendMessage("ここまでにします。今までの話で、覚えておくべきことをまとめて記憶してください。")

  const startNaming = async () => {
    if (!nameInput.trim()) return
    setBusy(true)
    try {
      await saveProfile(idToken, { agentName: nameInput.trim() })
      setAgentName(nameInput.trim())
      setNaming(false)
      setInterviewMode("onboarding")
      setView("interview")
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const removeFact = async (idx: number) => {
    const next = memory.filter((_, i) => i !== idx)
    setMemory(next)
    await saveProfile(idToken, { memorySet: next })
  }

  const addFact = async () => {
    if (!newFact.trim()) return
    await saveProfile(idToken, { memoryAppend: [newFact.trim()] })
    setMemory((m) => [...m, newFact.trim()])
    setNewFact("")
  }

  // 振り返りメール受信トグル。memory の即時保存パターンに倣い、
  // チェック切替で即 saveProfile する。失敗時は表示を元に戻す。
  const toggleReflectionEmail = async (next: boolean) => {
    setReceiveReflectionEmail(next)
    try {
      await saveProfile(idToken, { receiveReflectionEmail: next })
    } catch (e) {
      setReceiveReflectionEmail(!next)
      setNote(e instanceof Error ? e.message : String(e))
    }
  }

  // 名前を付け直す（旧 Flask の改名機能パリティ）。改名の経緯（旧名→新名・由来）を
  // 記憶に残す仕様も踏襲する。
  const renameAgent = async () => {
    const n = renameTo.trim()
    if (!n) {
      setNote("新しい名前を入力してください")
      return
    }
    if (n === agentName) {
      setNote("同じ名前です")
      return
    }
    setBusy(true)
    try {
      const origin = renameOrigin.trim()
      const memo =
        `名前を「${agentName || "（未設定）"}」から「${n}」に変えてもらった` +
        (origin ? `（由来: ${origin}）` : "")
      await saveProfile(idToken, {
        agentName: n,
        nameOrigin: origin,
        memoryAppend: [memo],
      })
      setAgentName(n)
      setMemory((m) => [...m, memo])
      setRenameTo("")
      setRenameOrigin("")
      setNote(`名前を「${n}」に変更しました（経緯を記憶に残しました）`)
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // アップロードした画像を候補列に追加して選択状態にする（即時反映はしない＝確定は別ボタン）。
  // 間違えたとき用に各アップロード顔は × で消せる（プリセットは消せない）。
  const addUpload = (f: File) => {
    if (f.size > 5 * 1024 * 1024) {
      setNote("5MB 以下の画像にしてください")
      return
    }
    const url = URL.createObjectURL(f)
    setUploads((prev) => {
      const next = [...prev, { url, file: f }]
      setPicked({ kind: "upload", id: next.length - 1 })
      return next
    })
    setNote("候補に追加しました。『この顔に決める』で確定します")
  }

  const removeUpload = (idx: number) => {
    setUploads((prev) => {
      const u = prev[idx]
      if (u) URL.revokeObjectURL(u.url)
      return prev.filter((_, i) => i !== idx)
    })
    // 選択中だったら解除、後ろのインデックスは詰める（現在の avatar・他候補は壊さない）
    setPicked((p) =>
      p && p.kind === "upload"
        ? p.id === idx
          ? null
          : p.id > idx
            ? { ...p, id: p.id - 1 }
            : p
        : p
    )
  }

  // 選択中の候補を恒久 avatar として確定・保存する。アップロードと同一経路（uploadAvatar）。
  // existing（現在の画像のまま）は再アップロード不要でそのまま確定。
  const confirmFace = async () => {
    if (busy) return
    if (!picked) {
      setNote("顔を選んでください")
      return
    }
    setBusy(true)
    setNote("")
    try {
      if (picked.kind === "existing") {
        setNote("✓ この顔のままにしました")
        setView("setup")
        return
      }
      let file: File
      if (picked.kind === "preset") {
        const resp = await fetch(`/avatars/avatar-candidate-${picked.id}.png`)
        if (!resp.ok) throw new Error("候補画像の取得に失敗しました")
        file = new File([await resp.blob()], `avatar-candidate-${picked.id}.png`, {
          type: "image/png",
        })
      } else {
        const u = uploads[picked.id]
        if (!u) throw new Error("選択した画像が見つかりません")
        file = u.file
      }
      await uploadAvatar(idToken, file)
      setAvatarType("image")
      setNote("✓ この顔にしました（再読み込みで反映）")
      setView("setup")
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // 顔は AgentFace に集約（SVG ソースを raw テキスト表示しない・遷移時のコード片対策）
  const renderFace = (size: number) =>
    avatarType === "image" ? (
      <AgentFace avatarUrl={avatarUrl} size={size} />
    ) : (
      <AgentFace icon={iconSvg || profile?.icon} size={size} />
    )

  const titleFace = <span className="inline-flex w-8 h-8">{renderFace(32)}</span>
  const body = (
    <>
        {naming ? (
          <div className="space-y-3 py-4">
            <p className="text-sm">
              はじめまして。これから一緒に働くエージェントに<b>名前</b>を付けてください。
              名付けのあと、短いインタビューであなたのことを教えてください。
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="例: JARVIS"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
              />
              <Button disabled={busy || !nameInput.trim()} onClick={startNaming}>
                名付けてインタビューへ
              </Button>
            </div>
          </div>
        ) : view === "interview" ? (
          <>
            <div className="text-xs text-muted-foreground border-b pb-1">
              {interviewMode === "onboarding" ? "はじめてのインタビュー" : "再インタビュー"}（会話は Chat に一本化。ここは記憶づくりのための短い聞き取りです）
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 py-2 min-h-[200px] max-h-[40dvh]">
              {msgs.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  {m.text}
                </div>
              ))}
              {busy && <div className="text-xs text-muted-foreground">…</div>}
              <div ref={bottomRef} />
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Input
                placeholder="インタビューに答える..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) send()
                }}
              />
              <Button disabled={busy || !input.trim()} onClick={send}>
                送信
              </Button>
              <Button variant="outline" disabled={busy} onClick={finishInterview}>
                ここまでにする
              </Button>
            </div>
          </>
        ) : view === "face" ? (
          <div className="flex-1 overflow-y-auto py-2 space-y-3">
            <div className="text-sm font-semibold">{agentName} の顔を決める</div>
            <div className="text-xs text-muted-foreground">
              クリックで選択 →「この顔に決める」で確定。手持ちの顔写真を使うのがおすすめです。
            </div>
            {/* 候補列: 現在の顔(image)＋このセッションのアップロード＋プリセット4枚。
                名前・役割ラベルは付けない（名前は名前欄で命名・独立）。 */}
            <div className="flex flex-wrap gap-2">
              {/* 現在保存済みの画像 avatar（あれば候補として残す・×は付けない＝現行avatarは壊さない） */}
              {avatarType === "image" && avatarUrl && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setPicked({ kind: "existing", id: 0 })}
                  className={`w-20 h-20 rounded-xl overflow-hidden border transition disabled:opacity-50 ${
                    picked?.kind === "existing" ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary"
                  }`}
                  title="今の顔"
                >
                  <img src={avatarUrl} alt="今の顔" className="w-full h-full object-cover" />
                </button>
              )}
              {/* このセッションでアップロードした顔（× で削除可能・間違えたとき用） */}
              {uploads.map((u, idx) => (
                <div key={u.url} className="relative w-20 h-20">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setPicked({ kind: "upload", id: idx })}
                    className={`w-20 h-20 rounded-xl overflow-hidden border transition disabled:opacity-50 ${
                      picked?.kind === "upload" && picked.id === idx
                        ? "ring-2 ring-primary"
                        : "hover:ring-2 hover:ring-primary"
                    }`}
                    title="アップロードした顔"
                  >
                    <img src={u.url} alt="アップロードした顔" className="w-full h-full object-cover" />
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => removeUpload(idx)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white text-xs leading-none shadow disabled:opacity-50"
                    title="この顔を候補から削除"
                    aria-label="この顔を候補から削除"
                  >
                    ×
                  </button>
                </div>
              ))}
              {/* プリセット候補4枚（削除不可） */}
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={busy}
                  onClick={() => setPicked({ kind: "preset", id: n })}
                  className={`w-20 h-20 rounded-xl overflow-hidden border transition disabled:opacity-50 ${
                    picked?.kind === "preset" && picked.id === n
                      ? "ring-2 ring-primary"
                      : "hover:ring-2 hover:ring-primary"
                  }`}
                  title="候補"
                >
                  <img
                    src={`/avatars/avatar-candidate-${n}.png`}
                    alt={`候補 ${n}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
            {/* アップロード導線（本筋）。即時反映でなく候補に追加して選択。 */}
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={busy} onClick={() => avatarRef.current?.click()}>
                📤 自分の画像をアップロード
              </Button>
              <span className="text-[11px] text-muted-foreground">
                png/jpg/webp・5MBまで。候補に追加されます
              </span>
              <input
                ref={avatarRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) addUpload(f)
                  e.target.value = ""
                }}
              />
            </div>
            <div className="flex items-center gap-2 border-t pt-2">
              <Button disabled={busy || !picked} onClick={confirmFace}>
                この顔に決める ★
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setView("setup")}>
                戻る
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-3 py-2">
            {/* 名前を付け直す（改名）。経緯（旧名→新名・由来）が記憶に残る */}
            <div className="rounded border p-2 space-y-1.5">
              <div className="text-xs font-semibold">名前を付け直す</div>
              <div className="flex gap-2">
                <Input
                  placeholder={`新しい名前（現在: ${agentName || "未設定"}）`}
                  value={renameTo}
                  onChange={(e) => setRenameTo(e.target.value)}
                />
                <Button size="sm" disabled={busy || !renameTo.trim()} onClick={renameAgent}>
                  変更
                </Button>
              </div>
              <Input
                placeholder="新しい名前の由来（任意・記憶に残ります）"
                value={renameOrigin}
                onChange={(e) => setRenameOrigin(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setView("face")}>
                顔を選び直す
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setInterviewMode("revisit")
                  setMsgs([])
                  setView("interview")
                }}
              >
                再インタビュー
              </Button>
            </div>
            <div>
              <div className="text-xs font-semibold mb-1">
                覚えていること（{memory.length}件）— あなたが直せる・消せる
              </div>
              <ul className="space-y-1 max-h-[30dvh] overflow-y-auto">
                {memory.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm rounded border px-2 py-1"
                  >
                    <span className="flex-1">{m}</span>
                    <button
                      className="text-destructive text-xs shrink-0"
                      onClick={() => removeFact(i)}
                      title="この記憶を消す"
                    >
                      削除
                    </button>
                  </li>
                ))}
                {memory.length === 0 && (
                  <li className="text-xs text-muted-foreground">まだ記憶はありません</li>
                )}
              </ul>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="覚えてほしいことを追加..."
                value={newFact}
                onChange={(e) => setNewFact(e.target.value)}
              />
              <Button size="sm" disabled={!newFact.trim()} onClick={addFact}>
                追加
              </Button>
            </div>
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={receiveReflectionEmail}
                  onChange={(e) => toggleReflectionEmail(e.target.checked)}
                  className="h-4 w-4"
                />
                <span>振り返りメールを受け取る</span>
              </label>
            </div>
          </div>
        )}
        {note && <div className="text-xs text-muted-foreground pt-1">{note}</div>}
    </>
  )

  // フルページ（管理ルート /admin/agent）: モーダルの枠を外し、画面の「格」で見せる。
  if (asPage) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-1">
        <div className="flex items-center gap-2 mb-2 text-xl font-bold">
          {titleFace}
          {agentName || "エージェント"}
        </div>
        {body}
      </div>
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {titleFace}
            {agentName || "エージェント"}
          </DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  )
}

/** ナレッジ管理（v2: タイトル + 説明 + 複数ファイル）。
 *
 *  一覧はアコーディオン。各タイトルを展開すると、説明編集（blur で保存）・ファイル一覧
 *  （個別削除）・ドロップゾーン（複数ファイル受け入れ）が出る。上部の「+ 新規タイトル」で
 *  scope + タイトル + 説明を入れて束を作り、後からファイルを足す。書込権限（team は admin・
 *  groups は所属者・personal は本人）はサーバ側 _valid_write_scope が実認可する。 */
export function KnowledgeDialog({
  idToken,
  onClose,
  asPage = false,
}: {
  idToken: string
  onClose: () => void
  // asPage=true のときはモーダル枠を外し、AdminLayout の主画面として描画する。
  // /admin/knowledge ルート（KnowledgeManagement.tsx）で使う。
  asPage?: boolean
}) {
  const [items, setItems] = useState<KnowledgeItem[]>([])
  const [teamAllowed, setTeamAllowed] = useState(false)
  const [groupOpts, setGroupOpts] = useState<{ scope: string; name: string }[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [note, setNote] = useState("")

  // 新規タイトルフォーム（インライン展開）。
  const [showNew, setShowNew] = useState(false)
  const [newScope, setNewScope] = useState("personal")
  const [newTitle, setNewTitle] = useState("")
  const [newDesc, setNewDesc] = useState("")
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      setItems(await listKnowledge(idToken))
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    }
  }, [idToken])

  // 登録スコープの選択肢（個人＋所属グループ、全体は admin のみ）。admin は全体を既定に。
  useEffect(() => {
    getKnowledgeScopes(idToken)
      .then((s) => {
        setGroupOpts(s.groups || [])
        setTeamAllowed(!!s.team)
        if (s.team) setNewScope("team")
      })
      .catch(() => {
        setGroupOpts([])
        setTeamAllowed(false)
      })
  }, [idToken])

  useEffect(() => {
    load()
  }, [load])

  // 表示用: scope ラベル（groups/{id} はグループ名 or 短縮 ID）
  const scopeLabel = (sc: string) => {
    if (sc === "team") return "全体"
    if (sc === "personal") return "個人"
    const g = groupOpts.find((o) => o.scope === sc)
    return g ? `G:${g.name}` : sc.replace("groups/", "G:")
  }

  const createTitle = async () => {
    if (!newTitle.trim()) {
      setNote("タイトルを入力してください")
      return
    }
    setCreating(true)
    setNote("")
    try {
      const { titleId } = await createKnowledgeTitle(
        idToken,
        newScope,
        newTitle.trim(),
        newDesc.trim()
      )
      setNewTitle("")
      setNewDesc("")
      setShowNew(false)
      await load()
      setExpanded(titleId) // 作ったら開いてファイル追加へ誘導
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e))
    } finally {
      setCreating(false)
    }
  }

  const intro = (
    <p className="text-xs text-muted-foreground">
      Chat と Board が同じ資料を参照します。1 つのタイトルに複数ファイルを束ねられます。
      エージェントは目次（タイトルと説明）を見て、必要なものだけ読みます。
      1 ファイル 15MB まで。
    </p>
  )

  // 本文（モーダル / フルページ 共通）。
  const body = (
    <>
      {intro}

      {/* 新規タイトル（インラインフォーム） */}
      <div className="border-b pb-2">
        {!showNew ? (
          <Button size="sm" variant="outline" onClick={() => setShowNew(true)}>
            <Plus size={16} className="mr-1" />
            新規タイトル
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <select
                className="h-9 rounded-md border bg-transparent px-2 text-sm"
                value={newScope}
                onChange={(e) => setNewScope(e.target.value)}
              >
                {/* 全体（会社ナレッジ）は admin 以上のみ。 */}
                {teamAllowed && <option value="team">全体（テナント共有）</option>}
                <option value="personal">個人</option>
                {groupOpts.map((g) => (
                  <option key={g.scope} value={g.scope}>
                    グループ: {g.name}
                  </option>
                ))}
              </select>
              <Input
                placeholder="タイトル（例: 営業資料パック）"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <Input
              placeholder="一行説明（何の資料か・任意）"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={creating} onClick={createTitle}>
                作成
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowNew(false)
                  setNewTitle("")
                  setNewDesc("")
                }}
              >
                キャンセル
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 一覧（アコーディオン） */}
      <div className="flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {items.map((k) => (
            <KnowledgeRow
              key={k.titleId}
              idToken={idToken}
              item={k}
              scopeLabel={scopeLabel}
              teamAllowed={teamAllowed}
              groupOpts={groupOpts}
              expanded={expanded === k.titleId}
              onToggle={() => setExpanded(expanded === k.titleId ? null : k.titleId)}
              onChanged={load}
            />
          ))}
          {items.length === 0 && (
            <li className="text-sm text-muted-foreground py-2">まだナレッジはありません</li>
          )}
        </ul>
      </div>
      {note && <div className="text-xs text-destructive">{note}</div>}
    </>
  )

  // フルページ（管理ルート /admin/knowledge）: モーダル枠を外し、画面の「格」で見せる。
  // 戻りは内部 state（expanded toggle）で完結し、画面遷移は発生しない。
  if (asPage) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
        <h2 className="text-xl font-bold mb-2">ナレッジ</h2>
        {body}
      </div>
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>ナレッジ</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  )
}

/** アップロード進行中ファイルの一時状態（タイトル展開部の dropzone 用）。 */
type UploadEntry = { name: string; status: "待機" | "送信中" | "完了" | "失敗"; file: File }

/** 一覧の 1 行（タイトル）。展開で説明編集・ファイル管理・dropzone を出す。
 *  useDropzone はフックなので行ごとに独立コンポーネント化している（親ループ内で呼べない）。 */
function KnowledgeRow({
  idToken,
  item,
  scopeLabel,
  teamAllowed,
  groupOpts,
  expanded,
  onToggle,
  onChanged,
}: {
  idToken: string
  item: KnowledgeItem
  scopeLabel: (sc: string) => string
  teamAllowed: boolean
  groupOpts: { scope: string; name: string }[]
  expanded: boolean
  onToggle: () => void
  onChanged: () => Promise<void> | void
}) {
  const [desc, setDesc] = useState(item.description || "")
  const [uploads, setUploads] = useState<UploadEntry[]>([])
  const [note, setNote] = useState("")
  const [changingScope, setChangingScope] = useState(false)
  // プレビューモーダル: クリックで開いた PreviewItem。null なら閉。
  const [preview, setPreview] = useState<PreviewItem | null>(null)
  // UI disable（scope select 等）に反映するための state。in-flight guard 自体は
  // 同期的に効かせる必要があるので useRef と併用する。
  const [uploading, setUploading] = useState(false)
  const on = item.enabled !== false // 全員フラグ（登録側の管理・全員の相棒に効く）
  const myOn = item.mutedByMe !== true // 自分フラグ（本人の相棒にだけ効く）
  // 編集可否（削除・説明編集・ファイル追加・全員トグル）。canEdit=false は
  // 「参照できるが登録者側ではない」項目＝閲覧モード＋「自分」トグルのみ。
  const canEdit = item.canEdit !== false
  // 同一 title への drop / retry の重なりを防ぐ in-flight guard。
  // /files 登録は index.json の read-modify-write なので、重なった onDrop が並走すると
  // 後勝ちで先の write を上書きする恐れがある（codex P2 指摘・2026-06-20）。
  const uploadingRef = useRef(false)

  // reload で item が差し替わったら説明ローカル state を同期する。
  useEffect(() => {
    setDesc(item.description || "")
  }, [item.description])

  // 1 ファイルをアップロードして status を進める。成功 true / 失敗 false。
  const runUpload = useCallback(
    async (file: File): Promise<boolean> => {
      setUploads((u) =>
        u.map((e) => (e.file === file ? { ...e, status: "送信中" } : e))
      )
      try {
        await uploadKnowledgeFile(idToken, item.titleId, file)
        setUploads((u) => u.map((e) => (e.file === file ? { ...e, status: "完了" } : e)))
        return true
      } catch (err) {
        setNote(err instanceof Error ? err.message : String(err))
        setUploads((u) => u.map((e) => (e.file === file ? { ...e, status: "失敗" } : e)))
        return false
      }
    },
    [idToken, item.titleId]
  )

  const onDropFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      if (uploadingRef.current) {
        setNote("アップロード中です。完了をお待ちください。")
        return
      }
      // scope 移動中の upload は旧 prefix に書かれて新 scope の metadata と齟齬を起こす
      // ので block する（codex round 2 P2 指摘・2026-06-20）。
      if (changingScope) {
        setNote("対象範囲を変更中です。完了してからアップロードしてください。")
        return
      }
      uploadingRef.current = true
      setUploading(true)
      setNote("")
      setUploads((u) => [
        ...u,
        ...files.map((f) => ({ name: f.name, status: "待機" as const, file: f })),
      ])
      try {
        // 部分成功許容（rollback しない）。失敗は再試行ボタンで個別に再送。
        // /files 登録は S3 index.json の read-modify-write なので並列化すると lost update が起きる。
        // 同一 title への drop は順次直列で送る（codex P1 指摘・2026-06-20）。
        for (const f of files) {
          await runUpload(f)
        }
        await onChanged() // 永続化済みファイルを一覧に反映
        // 完了したものはローカル一時一覧から落とす（失敗は再試行のため残す）。
        setUploads((u) => u.filter((e) => e.status !== "完了"))
      } finally {
        uploadingRef.current = false
        setUploading(false)
      }
    },
    [runUpload, onChanged]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    maxSize: 15 * 1024 * 1024,
    onDrop: onDropFiles,
    // 第一線の guard。onDropFiles 冒頭でも changingScope を弾く（多重防御）。
    disabled: changingScope || uploading,
  })

  const retry = async (entry: UploadEntry) => {
    if (uploadingRef.current) {
      setNote("アップロード中です。完了をお待ちください。")
      return
    }
    if (changingScope) {
      setNote("対象範囲を変更中です。完了してから再試行してください。")
      return
    }
    uploadingRef.current = true
    setUploading(true)
    try {
      const ok = await runUpload(entry.file)
      if (ok) {
        await onChanged()
        setUploads((u) => u.filter((e) => e.file !== entry.file))
      }
    } finally {
      uploadingRef.current = false
      setUploading(false)
    }
  }

  const badgeColor = (s: UploadEntry["status"]) =>
    s === "失敗"
      ? "text-destructive border-destructive"
      : s === "完了"
        ? "text-emerald-600 border-emerald-500"
        : "text-muted-foreground border-muted"

  // 全員トグル（登録側の管理・全員の相棒に効く）。
  const toggleForAll = async () => {
    setNote("")
    try {
      await updateKnowledgeTitle(idToken, item.titleId, { enabled: !on })
      await onChanged()
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err))
    }
  }
  // 自分トグル（個人ミュート・本人の相棒にだけ効く）。全員 OFF 中は意味を失うので無効化。
  const toggleForMe = async () => {
    setNote("")
    try {
      await setKnowledgeMuted(idToken, item.titleId, myOn)
      await onChanged()
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <li className={`rounded border text-sm ${on && myOn ? "" : "opacity-60"}`}>
      {/* タイトル行（クリックで展開・折り畳み） */}
      <div className="flex items-center gap-2 px-2 py-1">
        <button className="flex flex-1 items-center gap-2 text-left" onClick={onToggle}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-[10px] rounded bg-muted px-1">{scopeLabel(item.scope)}</span>
          <span className="flex-1 truncate font-medium">{item.title || "(無題)"}</span>
          <span className="text-xs text-muted-foreground">{item.files.length} ファイル</span>
        </button>
        {/* 参照トグルは二層。効き先をラベルに明記する（2026-07-05 山本さん決定）:
            「全員」= enabled（登録側の管理・OFF は全員の相棒から消える）
            「自分」= 個人ミュート（本人の相棒からだけ消える）
            個人スコープは見えるのが自分だけなので「全員」と同義＝1つに畳む。 */}
        {canEdit && (
          <button
            className={`text-xs rounded border px-2 py-0.5 ${
              on ? "border-emerald-500 text-emerald-600" : "border-muted text-muted-foreground"
            }`}
            title={
              item.scope === "personal"
                ? "自分の相棒が参照するか"
                : "全員に効く管理フラグ（OFF にすると全員の相棒から外れる）"
            }
            onClick={toggleForAll}
          >
            {item.scope === "personal"
              ? on
                ? "参照する"
                : "参照しない"
              : on
                ? "全員: 参照する"
                : "全員: 参照しない"}
          </button>
        )}
        {item.scope !== "personal" && (
          <button
            className={`text-xs rounded border px-2 py-0.5 ${
              !on
                ? "border-muted text-muted-foreground/50 cursor-not-allowed"
                : myOn
                  ? "border-emerald-500 text-emerald-600"
                  : "border-muted text-muted-foreground"
            }`}
            disabled={!on}
            title={
              !on
                ? "全体で参照停止中（全員フラグが OFF）"
                : "自分の相棒にだけ効く個人設定"
            }
            onClick={toggleForMe}
          >
            {myOn ? "自分: 参照する" : "自分: 参照しない"}
          </button>
        )}
        {canEdit && (
          <button
            className="text-destructive"
            title="タイトルを削除"
            onClick={async () => {
              if (
                !window.confirm(`「${item.title}」を削除しますか？（配下のファイルも消えます）`)
              )
                return
              setNote("")
              try {
                await deleteKnowledgeTitle(idToken, item.titleId)
                await onChanged()
              } catch (err) {
                setNote(err instanceof Error ? err.message : String(err))
              }
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-2 border-t px-2 py-2">
          {/* 閲覧モード（canEdit=false）: 参照できる資料の中身は見せるが編集系は出さない */}
          {!canEdit && (
            <p className="text-xs text-muted-foreground">
              この資料は閲覧のみです（登録者側が管理しています）。ファイルのプレビュー・
              ダウンロードと「自分: 参照する/しない」の切替ができます。
            </p>
          )}
          {/* 対象範囲（変更は S3 ファイル移動を伴うので confirm を挟む） */}
          {canEdit && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">対象範囲:</span>
            <select
              className="h-8 rounded-md border bg-transparent px-2 text-sm"
              disabled={changingScope || uploading}
              value={item.scope}
              onChange={async (e) => {
                const newScope = e.target.value
                if (newScope === item.scope) return
                // upload と scope 変更が重なると presigned PUT は旧 prefix へ書かれ、
                // 後段の /files 登録は新 scope の prefix で metadata を記録するので、
                // 実体が無い key が一覧に出てしまう（codex P2 指摘・2026-06-20）。
                // disable は上の disabled={... || uploading} で防いでいるが、保険で再チェック。
                if (uploadingRef.current) {
                  setNote("アップロード中です。完了してから変更してください。")
                  return
                }
                const msg =
                  `「${item.title}」の対象範囲を` +
                  `\n  [${scopeLabel(item.scope)}] → [${scopeLabel(newScope)}]\n` +
                  `に変更します。${item.files.length} ファイルが移動します。よろしいですか？`
                if (!window.confirm(msg)) {
                  // cancel: item.scope のままなので reload は不要
                  return
                }
                setNote("")
                setChangingScope(true)
                try {
                  await updateKnowledgeTitle(idToken, item.titleId, { scope: newScope })
                  await onChanged()
                } catch (err) {
                  setNote(err instanceof Error ? err.message : String(err))
                  await onChanged() // 失敗時も UI を整合
                } finally {
                  setChangingScope(false)
                }
              }}
            >
              {teamAllowed && <option value="team">全体（テナント共有）</option>}
              <option value="personal">個人</option>
              {groupOpts.map((g) => (
                <option key={g.scope} value={g.scope}>
                  グループ: {g.name}
                </option>
              ))}
            </select>
            {changingScope && (
              <span className="text-xs text-muted-foreground">移動中…</span>
            )}
          </div>
          )}

          {/* 説明（blur で保存・編集は登録者側のみ） */}
          {canEdit ? (
            <Input
              placeholder="一行説明（何の資料か）"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onBlur={async () => {
                if (desc === (item.description || "")) return
                setNote("")
                try {
                  await updateKnowledgeTitle(idToken, item.titleId, { description: desc })
                  await onChanged()
                } catch (err) {
                  setNote(err instanceof Error ? err.message : String(err))
                }
              }}
            />
          ) : (
            item.description && (
              <p className="text-xs text-muted-foreground">{item.description}</p>
            )
          )}

          {/* ファイル一覧（個別削除） */}
          {item.files.length > 0 && (
            <ul className="space-y-1">
              {item.files.map((f) => (
                <li
                  key={f.filename}
                  className="flex items-center gap-2 rounded bg-muted/40 px-2 py-1"
                >
                  {/* ファイル名クリックでプレビュー（Board 成果物と同じ PreviewModal を流用） */}
                  <button
                    className="flex-1 truncate text-left hover:underline"
                    title="プレビュー"
                    onClick={async () => {
                      setNote("")
                      try {
                        const meta = await getKnowledgeFileUrl(
                          idToken,
                          item.titleId,
                          f.filename
                        )
                        setPreview(meta)
                      } catch (err) {
                        setNote(err instanceof Error ? err.message : String(err))
                      }
                    }}
                  >
                    {f.filename}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {formatSizeKB(f.size)}
                  </span>
                  {/* ダウンロード（presigned URL を取得してブラウザに DL させる） */}
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    title="ダウンロード"
                    onClick={async () => {
                      setNote("")
                      try {
                        const meta = await getKnowledgeFileUrl(
                          idToken,
                          item.titleId,
                          f.filename
                        )
                        // <a download> をプログラマティックに発火（同タブ DL）
                        // cross-origin S3 では <a download> が無視されるので、サーバ側で
                        // attachment disposition を付けた downloadUrl を優先する（フォールバックは url）
                        const a = document.createElement("a")
                        a.href = meta.downloadUrl || meta.url
                        a.download = f.filename
                        a.rel = "noopener"
                        document.body.appendChild(a)
                        a.click()
                        a.remove()
                      } catch (err) {
                        setNote(err instanceof Error ? err.message : String(err))
                      }
                    }}
                  >
                    <Download size={14} />
                  </button>
                  {canEdit && (
                    <button
                      className="text-destructive text-xs"
                      onClick={async () => {
                        setNote("")
                        try {
                          await deleteKnowledgeFile(idToken, item.titleId, f.filename)
                          await onChanged()
                        } catch (err) {
                          setNote(err instanceof Error ? err.message : String(err))
                        }
                      }}
                    >
                      削除
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* アップロード中・失敗の一時表示（状態 badge + 失敗は再試行） */}
          {uploads.length > 0 && (
            <ul className="space-y-1">
              {uploads.map((e) => (
                <li key={e.name} className="flex items-center gap-2 px-2 py-0.5 text-xs">
                  <span className="flex-1 truncate">{e.name}</span>
                  <span className={`rounded border px-1 ${badgeColor(e.status)}`}>
                    {e.status}
                  </span>
                  {e.status === "失敗" && (
                    <button className="underline" onClick={() => retry(e)}>
                      再試行
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* dropzone（複数ファイル受け入れ・追加は登録者側のみ） */}
          {canEdit && (
            <div
              {...getRootProps()}
              className={`flex cursor-pointer items-center justify-center gap-2 rounded border border-dashed px-3 py-4 text-xs text-muted-foreground ${
                isDragActive ? "border-primary bg-primary/5" : ""
              }`}
            >
              <input {...getInputProps()} />
              <Upload size={16} />
              ここにファイルをドロップ、またはクリックして選択（複数可）
            </div>
          )}

          {note && <div className="text-xs text-destructive">{note}</div>}
        </div>
      )}
      {preview && <PreviewModal item={preview} onClose={() => setPreview(null)} />}
    </li>
  )
}
